import type {
  ActiveComponentDiscount,
  BundleDiscountTier,
} from "@intelligems/ig-types/dtos/bundle-discount";
import type {
  ActiveUpsellDiscountType,
  UpsellDiscountType,
} from "@intelligems/ig-types/dtos/upsell-product-rules";
import type { ComponentConfig } from "@intelligems/ig-types/dtos/components";

import { toSingleTier } from "./discount-extension-bridge/discountSignature";

/**
 * Whether a discount will actually take money off, and the narrowing that says
 * so. These live here rather than in ig-types because they are runtime code:
 * the storefront plugin and the WASM bundle may only import TYPES from that
 * package, and both need to ask this question.
 */

/** A tiered discount pays out only if some tier is worth something. */
export function isActiveBundleDiscount(discount: {
  discountType: UpsellDiscountType;
  discountTiers?: BundleDiscountTier[];
}): discount is ActiveComponentDiscount {
  return (
    discount.discountType !== "none" &&
    (discount.discountTiers ?? []).some((tier) => tier.value > 0)
  );
}

/** The flat counterpart: one value, which must be present and positive. */
export function isActiveUpsellDiscount(discount: {
  discountType: UpsellDiscountType;
  discountValue?: number | null;
}): discount is {
  discountType: ActiveUpsellDiscountType;
  discountValue: number;
} {
  return (
    discount.discountType !== "none" &&
    discount.discountValue != null &&
    discount.discountValue > 0
  );
}

/** The one reader of both component discount shapes. */
export function getActiveComponentDiscountTiers(
  config: ComponentConfig
): ActiveComponentDiscount | null {
  if (config.type === "bundleOnsiteUpsell") {
    const discount = config.discount;
    return discount && isActiveBundleDiscount(discount)
      ? {
          discountType: discount.discountType,
          discountTiers: discount.discountTiers,
        }
      : null;
  }
  const discount = config.discount;
  return discount && isActiveUpsellDiscount(discount)
    ? {
        discountType: discount.discountType,
        discountTiers: toSingleTier(discount.discountValue),
      }
    : null;
}
